/** @file chainPose: locals through the exact FK chain into a PoseBuffer (18.3, 21.5, 21.9, 22.3). */
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import type { Quat } from '@zilo/pose3d-math/quat/quat';
import { quatFromYawPitchRoll } from '@zilo/pose3d-math/quat/quatFromYawPitchRoll';
import { quatMirrorX } from '@zilo/pose3d-math/quat/quatMirrorX';
import { quatMul } from '@zilo/pose3d-math/quat/quatMul';
import { yawPitchOf } from '@zilo/pose3d-math/quat/yawPitchOf';
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import type { PoseBuffer, PoseParams } from '@zilo/pose3d-pose/contract';
import { applyHostTransform } from '@zilo/pose3d-pose/chain/applyHostTransform';
import { evalOrder } from '@zilo/pose3d-pose/chain/evalOrder';
import { rotateZScaled } from '@zilo/pose3d-pose/chain/rotateZScaled';
import { tiltOf } from '@zilo/pose3d-pose/chain/tiltOf';
import { POSE_FLAGS } from '@zilo/pose3d-pose/layout/poseFlags';
import { POSE_LAYOUT as L } from '@zilo/pose3d-pose/layout/poseLayout';
import type { Rig } from '@zilo/pose3d-pose/rig/rigTypes';
import type { LocalPose } from '@zilo/pose3d-pose/sample/localTypes';

const v3 = (a: Float64Array, i: number): Vec3 => [a[i * 3] ?? 0, a[i * 3 + 1] ?? 0, a[i * 3 + 2] ?? 0];
const q4 = (a: Float64Array, i: number): Quat => [a[i * 4] ?? 0, a[i * 4 + 1] ?? 0, a[i * 4 + 2] ?? 0, a[i * 4 + 3] ?? 1];
const add = (a: Vec3, b: Vec3): Vec3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const mirrored = (v: Vec3, on: boolean): Vec3 => (on ? [-v[0], v[1], v[2]] : v);
/** The node's bone origin is its own point (22.3 a): an originOffset or this frame's key translation. */
const ownOrigin = (rig: Rig, i: number, keyPos: Vec3): boolean => keyPos[0] !== 0 || keyPos[1] !== 0 || keyPos[2] !== 0
  || rig.originOffset[i * 3] !== 0 || rig.originOffset[i * 3 + 1] !== 0 || rig.originOffset[i * 3 + 2] !== 0;

/**
 * The rig-frame chain into `rig.workPos` / `workRot` / `workOrigin` / `workRoot` (21.5; decision 7): `rot = rot[p] *
 * local.rot`; bone origin `O = pos[p] + rz(rot[p], originOffset) + rz(rot[p], key.pos)` (the key translation turns
 * with the parent), node `N = O + rz(rot, node.pos * scale)`; a root `O = originOffset + key.pos`, `N = O + node.pos *
 * scale` (never turned by its own key). `rz` = `rotateZScaled` with `zScaleRotate`.
 */
function rigFrame(rig: Rig, locals: LocalPose, zs: number): void {
  for (const i of evalOrder(rig, locals, rig.workOrder)) {
    const local = locals[i];
    if (local === undefined) continue;
    const p = rig.workParent[i] ?? -1;
    const off = v3(rig.originOffset, i);
    const node = v3(rig.pos, i);
    const s = local.scale;
    const scaled: Vec3 = [node[0] * s, node[1] * s, node[2] * s];
    if (p < 0) {
      const origin = add(off, local.pos);
      rig.workRot.set(local.rot, i * 4);
      rig.workOrigin.set(origin, i * 3);
      rig.workPos.set(add(origin, scaled), i * 3);
      rig.workRoot[i] = i;
      continue;
    }
    const pr = q4(rig.workRot, p);
    const rot = quatMul(pr, local.rot);
    const origin = add(add(v3(rig.workPos, p), rotateZScaled(pr, off, zs)), rotateZScaled(pr, local.pos, zs));
    rig.workRot.set(rot, i * 4);
    rig.workOrigin.set(origin, i * 3);
    rig.workPos.set(add(origin, rotateZScaled(rot, scaled, zs)), i * 3);
    rig.workRoot[i] = rig.workRoot[p] ?? p;
  }
}

/**
 * Locals of every node through the chain into `out` (entity-relative, unrounded, POSE_LAYOUT). Per node: mirror
 * (x -> -x) first; then the tilt (21.9, `tiltOf`) turns each root's subtree about the root's pivot C (`tiltPivot`
 * `boneOrigin` = the root's bone origin, default, or `node` = its position): `p <- C + rz(tilt, p - C)` for positions
 * and bone origins, `q <- tilt * q`; then the facing `face` = yaw `bodyFacingDeg`: `rz(face, p)`, `face * q`; then the
 * host's `nodeYawDeg` turns and `partTransform` point map (22.3, `applyHostTransform`, parent-first order). Columns:
 * position, rotation, scale, bone origin `ox oy oz` (21.5), yaw/pitch read back from `rotation * rest` (own or
 * inherited, 18.2; 21.1), effective parent, flags (hidden 1, rotToggle 2, |pitch| > 90 4, frame key in bits 8-15).
 * Throws `VALIDATION` when `out` is shorter than `nodes * stride`.
 */
export function chainPose(rig: Rig, locals: LocalPose, params: PoseParams, out: PoseBuffer): PoseBuffer {
  const n = rig.names.length;
  if (out.length < n * L.stride) {
    throwError(ERROR_CODES.VALIDATION, { need: n * L.stride, got: out.length }, { hint: 'out holds nodes * POSE_LAYOUT.stride doubles' });
  }
  const zs = params.profile.zScaleRotate ?? 1;
  rigFrame(rig, locals, zs);
  const face = quatFromYawPitchRoll(params.bodyFacingDeg, 0, 0);
  const tilt = tiltOf(params);
  const pivots = params.tiltPivot === 'node' ? rig.workPos : rig.workOrigin;
  const mirror = params.mirrorX === true;
  const body = (v: Vec3, root: number): Vec3 => {
    if (tilt === null) return v;
    const c = mirrored(v3(pivots, root), mirror);
    return add(c, rotateZScaled(tilt, [v[0] - c[0], v[1] - c[1], v[2] - c[2]], zs));
  };
  const host = params.nodeYawDeg !== undefined || params.partTransform !== undefined;
  for (const i of rig.workOrder) {
    const local = locals[i];
    if (local === undefined) continue;
    const root = rig.workRoot[i] ?? i;
    const wp = rotateZScaled(face, body(mirrored(v3(rig.workPos, i), mirror), root), zs);
    const wo = rotateZScaled(face, body(mirrored(v3(rig.workOrigin, i), mirror), root), zs);
    const rot = mirror ? quatMirrorX(q4(rig.workRot, i)) : q4(rig.workRot, i);
    const wr = quatMul(face, tilt === null ? rot : quatMul(tilt, rot));
    const o = i * L.stride;
    out[o + L.px] = wp[0]; out[o + L.py] = wp[1]; out[o + L.pz] = wp[2];
    out[o + L.qx] = wr[0]; out[o + L.qy] = wr[1]; out[o + L.qz] = wr[2]; out[o + L.qw] = wr[3];
    out[o + L.ox] = wo[0]; out[o + L.oy] = wo[1]; out[o + L.oz] = wo[2];
    if (host) applyHostTransform(rig, i, params, ownOrigin(rig, i, local.pos), out);
    const q: Quat = host ? [out[o + L.qx] ?? 0, out[o + L.qy] ?? 0, out[o + L.qz] ?? 0, out[o + L.qw] ?? 1] : wr;
    const [yaw, pitch] = yawPitchOf(quatMul(q, q4(rig.restQuat, i)));
    out[o + L.scale] = local.scale;
    out[o + L.yaw] = yaw;
    out[o + L.pitch] = pitch;
    out[o + L.parentIndex] = rig.workParent[i] ?? -1;
    out[o + L.flags] = (local.hidden ? POSE_FLAGS.hidden : 0) | (local.rotToggle ? POSE_FLAGS.rotToggle : 0)
      | (Math.abs(pitch) > 90 ? POSE_FLAGS.yawFlipped : 0) | ((local.frameKey & POSE_FLAGS.frameKeyMask) << POSE_FLAGS.frameKeyShift);
  }
  return out;
}
